'use client'

import { useUser } from '@clerk/nextjs'
import { useEffect, useRef } from 'react'
import { trackSignupConversion } from '@/lib/site/gtm'

const SIGNUP_DEDUP_KEY_PREFIX = 'gtm_signup_conversion_'
const SIGNUP_FRESH_WINDOW_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Fires `brew_signup_conversion` exactly once for accounts created
 * within the last 5 minutes. Mounted directly under `ClerkProvider` so it can
 * fire as soon as Clerk completes signup, before organization onboarding.
 *
 * Dedup is per `userId` via `localStorage`, so refreshing the dashboard
 * or visiting `/plan` immediately after signup never re-fires.
 */
export function SignupConversionTracker() {
  const { isLoaded, isSignedIn, user } = useUser()
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current) {
      return
    }
    if (!isLoaded || !isSignedIn || !user) {
      return
    }

    const createdAt = user.createdAt?.getTime?.()
    if (typeof createdAt !== 'number') {
      return
    }

    const ageMs = Date.now() - createdAt
    if (ageMs > SIGNUP_FRESH_WINDOW_MS) {
      return
    }

    if (typeof window === 'undefined') {
      return
    }
    const dedupKey = `${SIGNUP_DEDUP_KEY_PREFIX}${user.id}`
    try {
      if (window.localStorage.getItem(dedupKey)) {
        return
      }
    } catch {
      // localStorage may be disabled — proceed anyway. The stable transaction
      // ID still gives Google a cross-tab deduplication key.
    }

    firedRef.current = true
    trackSignupConversion({
      transaction_id: `signup-${user.id}`,
      user_id: user.id,
      value: 0,
      currency: 'USD',
      plan_name: 'Free',
      created_at: createdAt,
    })

    try {
      window.localStorage.setItem(dedupKey, String(Date.now()))
    } catch {
      // Best-effort browser dedup; Google also receives transaction_id.
    }
  }, [isLoaded, isSignedIn, user])

  return null
}
